import * as React from 'react';
import {View, StyleSheet, ScrollView, Text} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../ScreenContext';
import BackSubheader from '../elements/BackSubheader';
import KeyboardAvoidingView from '../elements/365KeyboardAvoidingView';
import AVTextInput from '../elements/AVTextInput';
import RoundedButton, {ButtonType} from '../elements/RoundedButton';
import Styles from '../Styles';
import ActionsFactory from 'src/actions/ActionsFactory';
import NavActions from 'src/actions/NavActions';
import AccountStore from 'src/stores/AccountStore';
import Localized from 'src/constants/AppStrings';
import {alertError, confirm} from '../helpers/AlertHelper';

const LOCATION_CODE_MAX_LENGTH = 12;
const LOCATION_CODE_MIN_LENGTH = 1;

type LocationCodeScreenProps = Record<string, never>;

type LocationCodeScreenState = {
  locationCode: string;
};

class LocationCodeScreen extends React.Component<
  LocationCodeScreenProps,
  LocationCodeScreenState
> {
  static contextType = ScreenContext;

  constructor(props: LocationCodeScreenProps) {
    super(props);
    this.state = {
      locationCode: '',
    };
    this.saveClick = this.saveClick.bind(this);
  }

  async saveClick() {
    if (
      this.state.locationCode &&
      this.state.locationCode.length >= LOCATION_CODE_MIN_LENGTH
    ) {
      const homeLocation = AccountStore.getLocationId();
      const response = await ActionsFactory.getAccountActions().getLocation(
        this.state.locationCode,
      );
      const location = response.data;

      if (!location || !location.name) {
        alertError(
          Localized.Errors.dont_know_location_code,
          null,
          () => {},
          Localized.Errors.invalid_location_code,
        );
      } else if (location.hasOrderAhead) {
        confirm(
          this.getLocationAddressDisplay(location),
          async () => {
            if (!homeLocation) {
              // Make the location the user's home location
              await ActionsFactory.getAccountActions().getLocation(
                this.state.locationCode,
                AccountStore.getAccountId(),
              );
            }

            ActionsFactory.getAccountActions().addLocationLink(
              AccountStore.getAccountId(),
              location.id,
            );
            NavActions.pop();
          },
          () => {},
          location.name,
          Localized.Labels.back,
          Localized.Buttons.confirm,
        );
      } else if (homeLocation) {
        alertError(
          Localized.Errors.already_linked_to_home_location,
          null,
          () => {},
          Localized.Errors.oops,
        );
      } else {
        confirm(
          this.getLocationAddressDisplay(location),
          async () => {
            // Make the location the user's home location
            await ActionsFactory.getAccountActions().getLocation(
              this.state.locationCode,
              AccountStore.getAccountId(),
            );
            NavActions.pop();
          },
          () => {},
          location.name,
          Localized.Labels.back,
          Localized.Buttons.confirm,
        );
      }
    } else {
      alertError(
        Localized.Errors.dont_know_location_code,
        null,
        () => {},
        Localized.Errors.invalid_location_code,
      );
    }
  }

  getLocationAddressDisplay(location) {
    if (location.city && location.state) {
      return `${location.city}, ${location.state}`;
    } else if (location.city) {
      return location.city;
    } else if (location.state) {
      return location.state;
    }

    return '';
  }

  render() {
    return (
      <BackSubheader title={Localized.Labels.link_new_location}>
        <KeyboardAvoidingView behavior="height" insideTab>
          <ScrollView>
            <View style={styles.content}>
              <Text style={styles.headerText}>
                {Localized.Labels.enter_your_location_code}
              </Text>
              <AVTextInput
                label={Localized.Labels.location_code}
                onChangeText={(text) =>
                  this.setState({
                    locationCode: text,
                  })
                }
                accessibilityLabel="Location Code"
                value={this.state.locationCode}
                autoCapitalize="none"
                title={Localized.Labels.please_enter_location_code}
                maxLength={LOCATION_CODE_MAX_LENGTH}
              />
            </View>
          </ScrollView>
          <RoundedButton
            accessibilityLabel="Save"
            buttonType={ButtonType.action}
            text={Localized.Buttons.save}
            onPress={this.saveClick}
          />
        </KeyboardAvoidingView>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingHorizontal: Styles.Spacing.m3,
  },
  headerText: {
    marginTop: Styles.Spacing.m3,
    fontSize: Styles.Fonts.f2,
    fontWeight: 'bold',
  },
});
export default withForwardedNavigationParams()(LocationCodeScreen);
